import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import {
  Menu, Progress, Button, Popup,
} from 'semantic-ui-react';
import {
  useAppSelector,
} from 'store';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import api from 'api';
import util from 'utils/utils';
import useTheme from 'theme/useTheme';

import OneDriveFileChooser from './OneDriveFileChooser';

const allowedTypes = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
  csv: 'text/csv',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  xml: 'application/xml',
  xml_text: 'text/xml',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
  odg: 'application/vnd.oasis.opendocument.graphics',
  jpg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  tiff: 'image/tiff',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  m2ts: 'video/avchd-stream',
  flv: 'video/x-flv',
};

const ChooserPopup = styled(Popup)`
  z-index:5;
  max-width:400px !important;
  .menu {
    max-width:400px !important;
    min-width:300px !important;
  }
`;

const uploadFile = (file, setUploadState = () => {}, forType, forId, onComplete = () => {}, onError, setOpen = () => {}) => {
  if (file) {
    setUploadState(prevState => ({ ...prevState, isUploading: true, uploadProgress: 1 }));
    const regex = new RegExp('[^a-zA-Z0-9_.]', 'g');
    const name = file.name.replace(regex, '-');
    api.uploads.generateFileUploadRequest({
      forType, forId, name, size: file.size, type: file.type,
    }, (response) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', response.signedRequest);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadState(prevState => ({ ...prevState, uploadProgress: parseInt((e.loaded * 100) / e.total, 10) }));
        }
      });
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 100 }));
          if (xhr.status === 200) {
            setOpen(false);
            onComplete(response.fileName, name, response.signedDownloadRequest);
          } else if (onError) {
            onError('Unable to upload file');
          }
        }
      };
      xhr.send(file);
    }, (err) => {
      setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 0 }));
      if (onError) onError(err.message || 'Unable to upload file');
      else toast.error(err.message);
    });
  }
};

const FileChooser = (props) => {
  const {
    isOpen, onOpen, onClose, trigger, content, forType, forId, onComplete, onError, isUploading: propIsUploadLoading, uploadProgress: propUploadProgress, t, popupProps = {},
  } = props;
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    uploadProgress: 0,
  });
  const { isUploading: stateIsUploading, uploadProgress: stateUploadProgress } = uploadState;
  const isUploading = (propIsUploadLoading === undefined) ? stateIsUploading : propIsUploadLoading;
  const uploadProgress = (propUploadProgress === undefined) ? stateUploadProgress : propUploadProgress;
  const [open, setOpen] = useState(false);
  const [oneDriveOpen, setOneDriveOpen] = useState(false);
  const inputFile = useRef(null);
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);

  useEffect(() => {
    if (open !== isOpen && (typeof isOpen === 'boolean')) {
      setOpen(!!isOpen);
    }
  }, [open, isOpen]);

  const togglePopup = useCallback(() => {
    if (!open && onOpen) onOpen();
    if (open && onClose) onClose();
    setOpen(prevOpen => !prevOpen);
  }, [open, onOpen, onClose]);

  const storageFileChosen = useCallback((savedName, fileName, signedDownloadRequest) => {
    setOneDriveOpen(false);
    setOpen(false);
    onComplete(savedName, fileName, signedDownloadRequest);
  }, [setOneDriveOpen, setOpen, onComplete]);

  const chooseFile = useCallback(() => {
    inputFile.current.click();
  }, [inputFile]);

  return (
    <React.Fragment>
      <input
        type="file"
        accept={Object.values(allowedTypes).join(',')}
        style={{ display: 'none' }}
        ref={inputFile}
        onChange={e => uploadFile(e.target.files && e.target.files[0], setUploadState, forType, forId, onComplete, onError, setOpen)}
      />
      <OneDriveFileChooser
        {...props}
        open={oneDriveOpen}
        onComplete={storageFileChosen}
        onClose={() => {
          setOneDriveOpen(false);
          onClose && onClose();
        }}
      />

      <ChooserPopup
        style={{ zIndex: 5 }}
        on="click"
        open={open}
        position={theme.sizes.isMobile ? 'bottom center' : 'bottom left'}
        onClose={() => onClose ? null : setOpen(false)}
        trigger={trigger
          ? <div>{React.cloneElement(trigger, { loading: isUploading, onClick: togglePopup })}</div>
          : <Button size="small" color="teal" icon="file" fluid content={content || 'Choose a file'} loading={isUploading} onClick={togglePopup} />
        }
        content={(
          <div>
            <h5 style={{ marginTop: 0 }}>Upload a file</h5>
            <Menu vertical borderless>
              {isUploading && <Progress percent={uploadProgress} indicating progress style={{ margin: 5 }} />}
              <Menu.Item onClick={chooseFile}>
                <i className="upload icon" />
                {t('ideas.actions.chooseFile')}
              </Menu.Item>
              {util.userIsConnectedTo(user, 'office365') ? (
                <Menu.Item onClick={() => setOneDriveOpen(prevOpen => !prevOpen)} icon="microsoft" content="Choose from OneDrive" />
              ) : null}
            </Menu>

            <div style={{ textAlign: 'left', marginTop: 20 }}>
              <Button basic size="tiny" content={t('generic.cancel')} onClick={togglePopup} />
            </div>
          </div>
        )}
        {...popupProps}
      />
    </React.Fragment>
  );
};

export const DragAndDropFile = (props) => {
  const {
    forType, forId, onDragOver, onDragLeave, onComplete, onError, children, containerStyle, setUploadState, accept, disabled,
  } = props;
  const [dragItem, setDragItem] = useState(false);
  const dropRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleDragEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getFileFromEvent = (e) => {
    const files = [...e.dataTransfer.files];
    const filesWithPreview = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      file.preview = URL.createObjectURL(file);
      filesWithPreview.push(file);
    }

    return filesWithPreview[0];
  };

  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    setDragItem(null);
    onDragOver();
  }, [setDragItem, onDragOver, disabled]);

  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    clearTimeout(timeoutRef.current);
    setDragItem(null);
    onDragOver();
  }, [timeoutRef, setDragItem, onDragOver, disabled]);

  const handleDragLeave = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    timeoutRef.current = window.setTimeout(() => { setDragItem(null); onDragLeave(); }, 150);
  }, [timeoutRef, setDragItem, onDragLeave, disabled]);

  const handleDrop = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    const uploadItem = getFileFromEvent(e);
    if (uploadItem) {
      setDragItem(uploadItem);
      if (accept) {
        const { type } = uploadItem;
        if (accept === 'image/*') {
          if (type.indexOf('image') !== 0) {
            return toast.error('Only images are accepted');
          }
        } else {
          const videoType = type.replace('video/', '.');
          const acceptedTypes = accept.split(',');
          if (acceptedTypes.indexOf(videoType) === -1) {
            return toast.error('Only videos are accepted');
          }
        }
      }
      uploadFile(getFileFromEvent(e), setUploadState, forType, forId, onComplete, onError);
      onDragLeave();
    } else onDragLeave();
  }, [setDragItem, onDragLeave, setUploadState, forType, forId, onComplete, onError, accept, disabled]);

  useEffect(() => {
    const dropContainer = dropRef.current;
    dropContainer.addEventListener('dragenter', handleDragEnter);
    dropContainer.addEventListener('dragleave', handleDragLeave);
    dropContainer.addEventListener('dragover', handleDragOver);
    dropContainer.addEventListener('drop', handleDrop);
    return () => {
      dropContainer.removeEventListener('dragenter', handleDragEnter);
      dropContainer.removeEventListener('dragleave', handleDragLeave);
      dropContainer.removeEventListener('dragover', handleDragOver);
      dropContainer.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  useEffect(() => {
    if (dragItem) {
      onDragLeave();
    }
  }, [dragItem, setUploadState, forType, forId, onComplete, onError, onDragLeave]);

  return (
    <div ref={dropRef} style={containerStyle}>
      {children}
    </div>
  );
};

export default withTranslation()(FileChooser);
